/**
 * IntegrityDashboard — admin surface for fake-account / duplicate flags.
 *
 * Methodology hold #4: surface, don't auto-ban. Admin reads the cluster,
 * checks the family link context, and decides: dismiss, confirm fraud, or
 * ban-and-rollback (which removes the fraud teacher's votes + contributions).
 */
import { useEffect, useState } from 'react';
import { Shield, AlertTriangle, Loader2, X, Ban, ShieldCheck, History } from 'lucide-react';
import api from '../lib/api';

export default function IntegrityDashboard() {
  const [filter, setFilter] = useState('open');
  const [flags, setFlags] = useState([]);
  const [busy, setBusy] = useState(true);
  const [active, setActive] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [note, setNote] = useState('');
  const [msg, setMsg] = useState('');

  const reload = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/admin/integrity/flags?status=${filter}`);
      setFlags(r.data || []);
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Could not load flags');
    }
    setBusy(false);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [filter]);

  const openFlag = async (f) => {
    setActive(f);
    setNote('');
    setMsg('');
    try {
      const r = await api.get(`/admin/integrity/sessions/${f.user_id}`);
      setSessions(r.data || []);
    } catch {
      setSessions([]);
    }
  };

  const decide = async (decision) => {
    if (!active) return;
    if (decision !== 'dismiss' && note.trim().length < 10) {
      setMsg('Please add a 10+ char decision note (audit trail).');
      return;
    }
    try {
      const r = await api.post('/admin/integrity/decide', {
        flag_id: active.id, decision, note: note.trim(),
      });
      setMsg(`Decision logged: ${r.data?.status}`);
      setActive(null);
      reload();
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Decision failed');
    }
  };

  return (
    <div className="p-4 md:p-6" data-testid="integrity-dashboard">
      <div className="flex items-center gap-2 mb-2">
        <Shield size={16} className="text-[#D4A84A]" />
        <span className="font-hud text-[10px] md:text-xs text-[#D4A84A] tracking-widest uppercase">Account Integrity</span>
      </div>
      <h2 className="font-historical text-[#D4A84A] text-3xl md:text-4xl lg:text-5xl font-light tracking-tight mb-1">
        Integrity Flags
      </h2>
      <p className="font-mono text-xs md:text-sm text-white/65 mb-4">
        Surfaced, not auto-banned. Family-aware IP correlation. Decide with the audit trail.
      </p>

      <div className="flex items-center gap-2 mb-4">
        {['open', 'dismissed', 'confirmed_fraud', 'all'].map((s) => (
          <button
            key={s}
            data-testid={`integrity-filter-${s}`}
            onClick={() => { setFilter(s); setActive(null); }}
            className={`px-3 py-1.5 font-hud text-[10px] md:text-xs tracking-widest uppercase rounded transition-colors ${
              filter === s ? 'text-[#D4A84A] border border-[#D4A84A]/40 bg-[#D4A84A]/5' : 'text-white/65 border border-transparent hover:text-[#D4A84A]'
            }`}
          >
            {s.replace('_', ' ')}
          </button>
        ))}
      </div>

      {msg && (
        <div className="mb-4 px-3 py-2 border border-[#2F8A92]/25 bg-[#2F8A92]/05 rounded font-mono text-xs md:text-sm text-white/85">{msg}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* List */}
        <div className="border border-[#2F8A92]/15 rounded">
          <div className="px-3 py-2 border-b border-[#2F8A92]/10 font-hud text-[10px] md:text-xs text-[#2F8A92] tracking-widest uppercase">
            Flags ({flags.length})
          </div>
          {busy ? (
            <div className="p-6 text-center"><Loader2 size={18} className="animate-spin text-[#2F8A92] mx-auto" /></div>
          ) : flags.length === 0 ? (
            <div className="p-6 font-mono text-xs md:text-sm text-white/65 text-center">No flags in {filter}.</div>
          ) : (
            <ul className="divide-y divide-[#2F8A92]/8 max-h-[600px] overflow-y-auto">
              {flags.map((f) => (
                <li key={f.id}>
                  <button
                    data-testid={`integrity-row-${f.id}`}
                    onClick={() => openFlag(f)}
                    className={`w-full text-left px-3 py-3 hover:bg-[#2F8A92]/5 ${
                      active?.id === f.id ? 'bg-[#2F8A92]/10' : ''
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <AlertTriangle size={13} className="text-[#D4A84A] mt-1 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-sm text-white truncate">{f.user?.email || f.user_id}</div>
                        <div className="font-mono text-[10px] md:text-xs text-white/55 mt-0.5">
                          {f.ip} · {f.kind} · {new Date(f.created_at).toLocaleString()}
                        </div>
                        <ul className="mt-1.5 space-y-0.5">
                          {f.reasons.map((r, i) => (
                            <li key={i} className="font-mono text-[10px] md:text-xs text-[#D4A84A]">{r}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Detail + decision */}
        <div className="border border-[#D4A84A]/15 rounded">
          {!active ? (
            <div className="p-8 text-center font-mono text-sm md:text-base text-white/65">
              Select a flag to review the cluster + decide.
            </div>
          ) : (
            <div className="p-4 md:p-5 max-h-[600px] overflow-y-auto">
              <div className="font-historical text-xl md:text-2xl text-[#D4A84A]">{active.user?.email || active.user_id}</div>
              <div className="font-mono text-[11px] text-white/55 mt-1">
                Role: {active.user?.role || '?'} · Email verified: {String(!!active.user?.email_verified)} · Teacher: {active.user?.teacher_status || 'no'}
              </div>

              <div className="mt-4">
                <div className="font-hud text-[10px] tracking-widest text-[#2F8A92] uppercase mb-1">Reasons</div>
                <ul className="space-y-1">
                  {active.reasons.map((r, i) => (
                    <li key={i} className="font-mono text-xs text-white/85">• {r}</li>
                  ))}
                </ul>
              </div>

              {active.ip_cluster?.length > 0 && (
                <div className="mt-4">
                  <div className="font-hud text-[10px] tracking-widest text-white/65 uppercase mb-1">IP cluster (other unrelated accounts)</div>
                  <ul className="space-y-0.5 font-mono text-[11px] text-white/65 break-all">
                    {active.ip_cluster.map((u, i) => <li key={i}>• {u}</li>)}
                  </ul>
                </div>
              )}

              {active.fingerprint_cluster?.length > 0 && (
                <div className="mt-4">
                  <div className="font-hud text-[10px] tracking-widest text-white/65 uppercase mb-1">Fingerprint cluster</div>
                  <ul className="space-y-0.5 font-mono text-[11px] text-white/65 break-all">
                    {active.fingerprint_cluster.map((u, i) => <li key={i}>• {u}</li>)}
                  </ul>
                </div>
              )}

              <div className="mt-4">
                <div className="font-hud text-[10px] tracking-widest text-white/65 uppercase mb-1 flex items-center gap-1">
                  <History size={11} /> Recent sessions ({sessions.length})
                </div>
                <ul className="max-h-[140px] overflow-y-auto space-y-0.5 font-mono text-[10px] text-white/55">
                  {sessions.map((s) => (
                    <li key={s.id}>{s.kind} · {s.ip} · {new Date(s.created_at).toLocaleString()}</li>
                  ))}
                </ul>
              </div>

              {active.status === 'open' && (
                <div className="mt-5 pt-4 border-t border-[#D4A84A]/15 space-y-3">
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Decision note (required for confirm-fraud and ban-and-rollback)…"
                    rows={2}
                    data-testid="integrity-decision-note"
                    className="w-full bg-[#0A0A0A] border border-[#D4A84A]/25 rounded px-3 py-2 font-mono text-xs text-white/85 outline-none focus:border-[#D4A84A]/55"
                  />
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => decide('dismiss')}
                      data-testid="integrity-dismiss-btn"
                      className="font-hud text-xs tracking-widest text-white/85 border border-[#2F8A92]/30 px-3 py-2 hover:text-[#2F8A92]"
                    >
                      <X size={11} className="inline mr-1" /> DISMISS
                    </button>
                    <button
                      onClick={() => decide('confirm_fraud')}
                      data-testid="integrity-confirm-btn"
                      className="font-hud text-xs tracking-widest text-[#FF6B6B] border border-[#FF6B6B]/30 px-3 py-2 hover:bg-[#FF6B6B]/8"
                    >
                      <ShieldCheck size={11} className="inline mr-1" /> CONFIRM FRAUD
                    </button>
                    <button
                      onClick={() => decide('ban_and_rollback')}
                      data-testid="integrity-rollback-btn"
                      className="font-hud text-xs tracking-widest text-[#0A0A0A] bg-[#FF6B6B] px-3 py-2 hover:bg-[#FF6B6B]/85"
                    >
                      <Ban size={11} className="inline mr-1" /> BAN + ROLLBACK
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
